import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Swords, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Loader } from '@/components/common/Loader';
import { useCodeTornei } from '@/contexts/CodeTorneiContext';
import { useEvidenzaSfida } from '@/hooks/useEvidenzaSfida';
import { useHaptics } from '@/hooks/useHaptics';
import { useIsVisible } from '@/hooks/useIsVisible';
import { useLimuloPolling, useLimuloPollingGuida } from '@/hooks/useLimuloPolling';
import { testoEsito, useEsitiSfide, useRispostaSfida, type AnnotaSfida, type Nota } from '@/hooks/useRispostaSfida';
import { useSfideInArrivo } from '@/hooks/useSfideInArrivo';
import { cn } from '@/lib/cn';
import { CHIAVE_COLLEGANDO_PROFILO, nomeDiChiSfida } from '@/lib/limulo/challenge';
import { challengeReasonOf, limuloFailure } from '@/lib/limulo/errors';
import { LIMULO_POLL_SFIDE_MS } from '@/lib/limulo/polling';
import { SlotPelleSfide } from '@/lib/limulo/slotPelleSfide';
import { perGioco } from '@/lib/limulo/perGioco';
import { gameByCode, labelOf } from '@/lib/limulo/profiles';
import { uscitaDallaCodaAccettando } from '@/lib/limulo/sfidaECoda';
import { limuloTournamentUrl } from '@/lib/limulo/routes';
import {
  SFIDE_INVIATE,
  faseDellaAccettata,
  nomeDellAltro,
  rifiutoRevocaSfidaOf,
  staAnnullabile,
} from '@/lib/limulo/sfideTab';
import {
  useGetLimuloChallengesQuery,
  useGetLimuloGamesQuery,
  useRevokeLimuloChallengeMutation,
} from '@/services/limuloApi';
import { useAppSelector } from '@/store/hooks';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * Cio' che il tab da' alle sue righe: la radice (per sapere se il focus e' GIA' dentro il tab) e `annuncia`, che la riga chiama
 * appena la regola ha deciso se il focus si sposta: senza focus `aria-current` su un `<li>` non viene annunciato, e l'evidenza
 * sarebbe MUTA per un lettore di schermo, quindi `annuncia(true)` scrive un testo nella regione `role="status"`, che parla al posto
 * del focus. `annuncia(false)` (il focus si e' spostato) lo toglie: col focus sulla riga il testo sarebbe un doppio annuncio.
 */
interface ContestoTab {
  radice: RefObject<HTMLElement | null>;
  annuncia: (mostra: boolean) => void;
}
const RadiceDelTab = createContext<ContestoTab | null>(null);

/**
 * **Le sfide di PIATTAFORMA nel tab «Sfide» di Amici** (T2-3, lotto B2): «In arrivo» e «Inviate».
 * Si monta SOLO sotto la serratura di piattaforma (Limulo + beta, `Friends.tsx`): fuori, non esiste.
 *
 * 🔑 **Il costo, MISURATO in `tests/sfide-tab-costo.test.tsx`**:
 *  · «In arrivo» NON ha un poll suo: legge la cache della barra (`useSfideInArrivo`, stessi argomenti) ⇒
 *    barra + tab = barra da sola;
 *  · «Inviate» (`direction=out`) si legge SOLO a tab aperto (questo componente e' montato solo li'), con le
 *    regole della barra: `LIMULO_POLL_SFIDE_MS`, backoff condiviso, fermo a pagina nascosta e UNA rilettura
 *    al ritorno. Aperto il tab: 1 GET subito, poi 1 ogni 60 s.
 *  · `GET /v1/games` (nome del gioco) solo se c'e' almeno una riga; `GET /v1/games/profiles` solo se c'e'
 *    almeno una sfida da accettare.
 * ⚠️ Freschezza, con un tetto: le inviate sono lette con `refetchOnMountOrArgChange: 60` — riaprendo il tab,
 * i dati in cache piu' vecchi di 60 s si rileggono SUBITO (al massimo +1 GET a ogni riapertura oltre i 60 s,
 * contata nel banco del costo); piu' recenti, si mostrano e il ritmo riparte dalla riapertura.
 * Quindi «fino a 60 s di ritardo» vale SOLO a tab APERTO (il ritmo e' quello della barra). Riaprendo entro 60 s
 * il dato mostrato puo' essere molto piu' vecchio: misurato, letto a 0 e riaperto a 40 s, la lettura successiva
 * e' a 100 s, quindi chi guarda vede un dato di ~2 minuti prima che arrivi la GET del ritmo.
 *
 * ⛔ Nessun orologio del telefono: lo stato lo dice il server, gia' quello EFFETTIVO (`azionePersistente.ts:19`).
 */
export function SfideDiPiattaformaTab({ soloGiochi }: { soloGiochi?: ReadonlySet<string> } = {}) {
  const { t } = useTranslation('general');
  // Il deep link `sfida=<id>` (B3b-1): l'id si confronta con le liste che l'utente ha gia', mai con una GET per id.
  const evidenza = useEvidenzaSfida();
  const radice = useRef<HTMLDivElement>(null);
  // Per ogni id: la regione deve dirlo a voce (il focus NON si e' spostato) o no (si e' spostato). Per id e non un solo valore:
  // un link a un'altra sfida con il focus libero non deve ereditare l'annuncio della precedente. `idCorrente` lo tiene fresco.
  const [annunciata, setAnnunciata] = useState<Record<string, boolean>>({});
  const idCorrente = useRef<string | null>(null);
  idCorrente.current = evidenza.id;
  const annuncia = useCallback((mostra: boolean) => {
    const id = idCorrente.current;
    if (id !== null) setAnnunciata((prev) => (prev[id] === mostra ? prev : { ...prev, [id]: mostra }));
  }, []);
  const contesto = useMemo(() => ({ radice, annuncia }), [annuncia]);
  return (
    <RadiceDelTab.Provider value={contesto}>
      <div ref={radice} className="flex flex-col gap-4" data-testid="sfide-tab-piattaforma">
        {evidenza.id !== null && (
          // La regione viva esiste GIA' quando arriva il link, VUOTA: un lettore di schermo annuncia i cambi dentro una regione
          // che c'era prima, non una regione che compare piena.
          <div role="status" data-testid="sfide-tab-evidenza-regione">
            {evidenza.assente && (
              // UNA frase neutra per ogni caso (scaduta, con risposta, di un altro, inventata): dal link non deve trapelare
              // niente su una sfida non tua. Non e' un errore e non ne ha lo stile.
              <p className={VUOTO} data-testid="sfide-tab-evidenza-assente">
                {t('social.sfida_link_non_disponibile')}
              </p>
            )}
            {evidenza.rigaVisibile && annunciata[evidenza.id] === true && (
              // Solo quando il focus NON si e' spostato sulla riga (campo, bottone fuori dal tab, dialogo aperto) E una riga e'
              // DAVVERO in evidenza adesso (`rigaVisibile`, non lo stato `trovata`, che il latch tiene anche quando la riga e'
              // uscita: accettata, scaduta). Col focus la riga si annuncia da sola. Non porta niente della sfida.
              <p className="sr-only" data-testid="sfide-tab-evidenza-trovata">
                {t('social.sfida_link_evidenziata')}
              </p>
            )}
          </div>
        )}
        <SezioneInArrivo evidenza={evidenza.id} soloGiochi={soloGiochi} />
        <SezioneInviate evidenza={evidenza.id} soloGiochi={soloGiochi} />
      </div>
    </RadiceDelTab.Provider>
  );
}

/** Un campo in cui si sta scrivendo: spostargli via il focus chiude la tastiera del telefono. */
function campoEditabile(el: Element): boolean {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)
    return true;
  const ce = el.getAttribute('contenteditable');
  return (
    (ce !== null && ce !== 'false') || el.getAttribute('role') === 'textbox' || el.getAttribute('role') === 'searchbox'
  );
}

/**
 * Il focus si sposta sulla riga SOLO se non ruba niente a nessuno: `activeElement` e' `body` o `null` (nessuno ha il focus), oppure sta
 * DENTRO il tab e non e' un campo editabile (un «Riprova», una riga); e non c'e' un dialogo `aria-modal` aperto. Altrimenti bastano
 * lo scroll e `aria-current`, e `annuncia` dice a voce, nella regione `role="status"`, che c'e' una riga in evidenza (senza focus un
 * `aria-current` su un `<li>` non si annuncia). Il campo di ricerca di Amici sta SOPRA le pastiglie, «Sfide» compresa: chi ci sta
 * scrivendo lo TIENE.
 */
function ilFocusPuoSpostarsi(radice: Element | null): boolean {
  if (document.querySelector('[aria-modal="true"]') !== null) return false;
  const attivo = document.activeElement;
  if (attivo === null || attivo === document.body) return true;
  return radice !== null && radice.contains(attivo) && !campoEditabile(attivo);
}

/**
 * La riga segnata dal deep link: `aria-current` (lo dice anche a chi non vede), una cornice, portata in vista al centro e poi
 * col FOCUS se `ilFocusPuoSpostarsi` (`tabIndex -1`, `preventScroll` perche' lo scroll l'ha gia' fatto): senza il focus
 * `aria-current` su un `<li>` non viene annunciato finche' nessuno ci arriva.
 * `scrollIntoView` si chiama SE esiste (jsdom non l'ha). Senza evidenza restituisce attributi vuoti: il DOM e' quello di prima.
 *
 * ⚠️ **`scroll-mb` NON garantisce che la riga non finisca sotto `EngagementNudge`** (decisione di tma-orcr, 29/9 21:22Z: «non
 * garantito», il caso limite e' nel suo backlog; `Friends.tsx` non si tocca qui). Cosa e' vero: la `TabBar` sta NEL FLUSSO sotto
 * `<main>` (lo scorrevole), quindi il centro dello scorrevole non la incontra. Cosa NON e' vero, misurato dal revisore su una
 * pagina modello in Chromium e WebKit (non sull'app): con `block: 'center'` lo `scroll-margin` non crea spazio dove servirebbe (una
 * riga a meta' lista si sposta di meta' del margine, l'ULTIMA riga resta dov'e': lo scroll e' gia' al massimo) e per farlo servirebbe
 * spazio in fondo al contenuto scorrevole. Inoltre la safe-area della classe e' quella di `TabBar`/`AppShell`, NON quella di
 * `EngagementNudge` (che usa solo `env()`), e 4.5rem e' la distanza del nudge dal fondo, non la sua cima. La classe resta per gli
 * altri casi; il caso «la riga in evidenza e' l'ultima e il nudge e' aperto» e' non garantito. In jsdom non c'e' geometria: si
 * asserisce la classe, non lo scorrimento.
 */
const SCROLL_MARGINE_FONDO =
  'scroll-mb-[calc(max(env(safe-area-inset-bottom,0px),var(--tg-viewport-safe-area-inset-bottom,0px))+4.5rem)]';
function useRigaEvidenziata(evidenziata: boolean) {
  const ref = useRef<HTMLLIElement>(null);
  const contesto = useContext(RadiceDelTab);
  useEffect(() => {
    if (!evidenziata) return;
    ref.current?.scrollIntoView?.({ block: 'center' });
    const sposta = ilFocusPuoSpostarsi(contesto?.radice.current ?? null);
    if (sposta) ref.current?.focus?.({ preventScroll: true });
    contesto?.annuncia(!sposta);
  }, [evidenziata, contesto]);
  return {
    ref,
    'aria-current': evidenziata ? ('true' as const) : undefined,
    tabIndex: evidenziata ? -1 : undefined,
    'data-evidenza': evidenziata ? 'true' : undefined,
    evidenziataClasse: evidenziata ? `ring-2 ring-brand ${SCROLL_MARGINE_FONDO}` : undefined,
  };
}

const TITOLO = 'mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted';
const VUOTO = 'card-edge rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted';

/** Il nome del gioco dal catalogo `/v1/games` (una volta, cache condivisa), col ripiego sul `gameId`. */
function useNomeGioco(haRighe: boolean): (gameId: string) => string {
  const giochi = useGetLimuloGamesQuery(undefined, { skip: !haRighe });
  const dati = giochi.data;
  return useCallback((gameId: string) => labelOf(gameByCode(gameId, dati ?? []), gameId), [dati]);
}

// ─────────────────────────────────────────────────────────────────────────────
// In arrivo
// ─────────────────────────────────────────────────────────────────────────────

function SezioneInArrivo({ evidenza, soloGiochi }: { evidenza: string | null; soloGiochi?: ReadonlySet<string> }) {
  const { t } = useTranslation('general');
  const { vive: tutte, isLoading, isError, rileggi } = useSfideInArrivo();
  const vive = useMemo(() => perGioco(tutte, soloGiochi), [tutte, soloGiochi]);
  const viveIds = useMemo(() => new Set(vive.map((c) => c.challengeId)), [vive]);
  // Come la barra: gli esiti stanno nel padre (`useEsitiSfide`), cosi' un'accettazione riuscita che la lista
  // ha gia' tolto resta leggibile `AVVISO_MS` invece di sparire muta.
  const { note, annota, togli } = useEsitiSfide(viveIds);
  const soloEsiti = Object.values(note).filter((n) => (n.tipo !== 'errore' || !viveIds.has(n.sfida.challengeId)) && (soloGiochi === undefined || soloGiochi.has(n.sfida.gameId)));
  const haRighe = vive.length > 0 || soloEsiti.length > 0;
  const nomeGioco = useNomeGioco(haRighe);
  const Pelle = useContext(SlotPelleSfide);

  return (
    <section data-testid="sfide-tab-in-arrivo">
      {Pelle?.Titolo ? <Pelle.Titolo sezione="ricevute" conteggio={vive.length} /> : <h2 className={TITOLO}>{t('social.incoming', { defaultValue: 'Incoming' })}</h2>}
      {isLoading && !haRighe ? (
        <div className="flex h-16 items-center justify-center">
          <Loader />
        </div>
      ) : isError && !haRighe ? (
        <ErroreDiLettura onRiprova={rileggi} testid="sfide-tab-in-arrivo-errore" />
      ) : !haRighe ? (
        <p className={VUOTO} data-testid="sfide-tab-in-arrivo-vuoto">
          {t('social.sfide_tab_in_arrivo_vuoto')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {vive.map((c) => {
            const n = note[c.challengeId];
            return (
              <RigaInArrivo
                key={c.challengeId}
                sfida={c}
                nomeGioco={nomeGioco(c.gameId)}
                errore={n?.tipo === 'errore' ? n.testo : null}
                avviso={null}
                evidenziata={evidenza !== null && c.challengeId === evidenza}
                annota={annota}
                azzera={togli}
              />
            );
          })}
          {soloEsiti.map((n) => (
            <RigaInArrivo
              key={n.sfida.challengeId}
              sfida={n.sfida}
              nomeGioco={nomeGioco(n.sfida.gameId)}
              errore={null}
              avviso={n}
              evidenziata={false}
              annota={annota}
              azzera={togli}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function RigaInArrivo({
  sfida,
  nomeGioco,
  errore,
  avviso,
  evidenziata,
  annota,
  azzera,
}: {
  sfida: LimuloChallengeWithIdentities;
  nomeGioco: string;
  /** L'errore dell'ultimo tocco (tenuto dal padre), a riga ancora offerta. */
  errore: string | null;
  /** Non `null` = la riga non e' piu' offerta: mostra l'esito coi pulsanti spenti. */
  avviso: Nota | null;
  /** La segna il deep link `sfida=<id>`. */
  evidenziata: boolean;
  annota: AnnotaSfida;
  azzera: (challengeId: string) => void;
}) {
  const { t } = useTranslation('general');
  const Pelle = useContext(SlotPelleSfide);
  const { onAccetta, onRifiuta, accettando, rifiutando, inLettura, collegando } = useRispostaSfida(sfida, annota, azzera);
  const { challengeId } = sfida;
  const nome = nomeDiChiSfida(sfida) ?? t('social.sfida_ricevuta_senza_nome', { defaultValue: 'A player' });
  const spento = accettando || rifiutando || inLettura || avviso !== null;
  const esito = avviso?.tipo === 'ok' ? avviso.testo : null;
  const testoErrore = avviso !== null && avviso.tipo !== 'ok' ? avviso.testo : errore;
  const { evidenziataClasse, ...segno } = useRigaEvidenziata(evidenziata);
  // Un avviso, non un dialogo: chi ha un biglietto in attesa per QUESTO gioco lo legge PRIMA di premere Accetta.
  // Solo su una riga offerta E PULITA: senza esito (a tocco fatto la coda l'ha gia' lasciata) e senza NESSUNA frase d'errore
  // a schermo (`testoErrore === null`). Dopo un 409 «abbinato in coda» il biglietto del client resta `waiting` fino al
  // rinnovo (20 s) e l'avviso contraddirebbe la frase; meglio tacere una frase vera che tenerne a schermo una falsa.
  const { ticket } = useCodeTornei();
  const avvisoCoda = avviso === null && testoErrore === null && uscitaDallaCodaAccettando(ticket, sfida);
  const idAvvisoCoda = `sfide-tab-avviso-coda-${challengeId}`;
  if (Pelle?.RigaRicevuta) return <Pelle.RigaRicevuta vista={{ sfida, nome, nomeGioco, spento, esito, testoErrore, avvisoCoda, idAvvisoCoda, collegando, onAccetta, onRifiuta, segno, evidenziataClasse }} />;

  return (
    <li
      {...segno}
      className={cn('rounded-xl bg-bg-secondary p-3', evidenziataClasse)}
      data-testid={`sfide-tab-riga-in-arrivo-${challengeId}`}
    >
      <div className="flex items-center gap-3">
        <IconaSfida />
        <p className="min-w-0 flex-1 text-sm font-semibold text-fg">
          {t('social.sfida_ricevuta_testo', { name: nome, game: nomeGioco })}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="icona"
            size={32}
            tono="neutro"
            disabled={spento}
            onClick={onRifiuta}
            data-testid={`sfide-tab-rifiuta-${challengeId}`}
            aria-label={t('social.sfida_rifiuta_aria', { name: nome, game: nomeGioco })}
          >
            <X aria-hidden className="h-4 w-4" />
          </Button>
          {/* `h-8 gap-1`: altezza fissa e spazio icona-testo di oggi, due override di pelle sulla `sm` (PR-2). */}
          <Button
            size="sm"
            forma="pastiglia"
            peso="bold"
            disabled={spento}
            onClick={onAccetta}
            data-testid={`sfide-tab-accetta-${challengeId}`}
            // Il nome accessibile dice DI CHI e' la sfida (le chiavi del DM): con due righe «Accetta» due volte non dice quale.
            // Il testo VISIBILE non cambia, ed e' contenuto nel nome (WCAG 2.5.3, «label in name»).
            aria-label={t('social.sfida_accetta_aria', { name: nome, game: nomeGioco })}
            aria-describedby={avvisoCoda ? idAvvisoCoda : undefined}
            className="h-8 gap-1"
          >
            <Check aria-hidden className="h-4 w-4" />
            {t('social.sfide_tab_accetta')}
          </Button>
        </div>
      </div>
      {avvisoCoda && (
        <p id={idAvvisoCoda} data-testid={idAvvisoCoda} className="mt-2 text-xs text-fg-muted">
          {t('social.sfida_accetta_avviso_coda', { game: nomeGioco })}
        </p>
      )}
      {collegando && esito === null && testoErrore === null && (
        <p role="status" data-testid={`sfide-tab-collegando-${challengeId}`} className="mt-2 text-xs text-fg-muted">
          {t(CHIAVE_COLLEGANDO_PROFILO)}
        </p>
      )}
      {esito !== null && (
        <p role="status" data-testid={`sfide-tab-esito-${challengeId}`} className="mt-2 text-xs font-medium text-brand">
          {esito}
        </p>
      )}
      {testoErrore !== null && (
        <p role="alert" data-testid={`sfide-tab-errore-${challengeId}`} className="mt-2 text-xs text-danger">
          {testoErrore}
        </p>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inviate
// ─────────────────────────────────────────────────────────────────────────────

function SezioneInviate({ evidenza, soloGiochi }: { evidenza: string | null; soloGiochi?: ReadonlySet<string> }) {
  const { t } = useTranslation('general');
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const polling = useLimuloPolling();
  // Nascosta = ZERO, non «al piu' uno»: `skipPollingIfUnfocused` lascia partire UN giro dopo che la pagina e'
  // andata in secondo piano (misurato per la barra, `tests/sfide-barra-piattaforma-costo.test.tsx`). Con
  // `pollingInterval: 0` il timer si toglie; al ritorno RTK rilegge UNA volta (`refetchOnFocus`).
  const visibile = useIsVisible();
  const lista = useGetLimuloChallengesQuery(SFIDE_INVIATE, {
    skip: !authed,
    // Riaperto il tab, i dati piu' vecchi di 60 s si rileggono subito (v. il docblock del componente).
    refetchOnMountOrArgChange: 60,
    ...polling.ritmo,
    pollingInterval: visibile ? polling.ritmo.pollingInterval : 0,
  });
  useLimuloPollingGuida(polling, lista, LIMULO_POLL_SFIDE_MS);
  const items = useMemo(() => perGioco(authed ? (lista.data?.items ?? []) : [], soloGiochi), [authed, lista.data, soloGiochi]);
  const nomeGioco = useNomeGioco(items.length > 0);
  const { refetch } = lista;
  const rileggi = useCallback(() => void refetch(), [refetch]);
  const Pelle = useContext(SlotPelleSfide);

  return (
    <section data-testid="sfide-tab-inviate">
      {Pelle?.Titolo ? <Pelle.Titolo sezione="inviate" conteggio={items.length} /> : <h2 className={TITOLO}>{t('social.outgoing', { defaultValue: 'Sent' })}</h2>}
      {lista.isLoading && items.length === 0 ? (
        <div className="flex h-16 items-center justify-center">
          <Loader />
        </div>
      ) : lista.isError && items.length === 0 ? (
        <ErroreDiLettura onRiprova={rileggi} testid="sfide-tab-inviate-errore" />
      ) : items.length === 0 ? (
        <p className={VUOTO} data-testid="sfide-tab-inviate-vuoto">
          {t('social.sfide_tab_inviate_vuoto')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((c) => (
            <RigaInviata
              key={c.challengeId}
              sfida={c}
              nomeGioco={nomeGioco(c.gameId)}
              evidenziata={evidenza !== null && c.challengeId === evidenza}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

type NotaInviata = { tipo: 'ok' | 'errore'; testo: string };

function RigaInviata({
  sfida,
  nomeGioco,
  evidenziata,
}: {
  sfida: LimuloChallengeWithIdentities;
  nomeGioco: string;
  /** La segna il deep link `sfida=<id>`. */
  evidenziata: boolean;
}) {
  const { t } = useTranslation('general');
  const Pelle = useContext(SlotPelleSfide);
  const haptics = useHaptics();
  const navigate = useNavigate();
  const [revoca, esitoRevoca] = useRevokeLimuloChallengeMutation();
  const [nota, setNota] = useState<NotaInviata | null>(null);
  /** La guardia anti-doppio-tap sta in una REF, come per Accetta: `disabled` e' un suggerimento. */
  const inVolo = useRef(false);
  const { challengeId, state } = sfida;
  const altro = nomeDellAltro(sfida);
  const nome = altro ?? t('social.sfide_tab_un_amico');

  const annulla = () => {
    if (inVolo.current) return;
    inVolo.current = true;
    setNota(null);
    revoca({ challengeId })
      .unwrap()
      // La lista si rilegge da sola (`revokeLimuloChallenge` invalida): fino ad allora il tocco ha il suo segnale.
      .then(() => setNota({ tipo: 'ok', testo: t('social.sfide_annulla_ok') }))
      .catch((e: unknown) => {
        const failure = limuloFailure(e);
        const motivo = challengeReasonOf(e);
        const esito = rifiutoRevocaSfidaOf(motivo, failure.code ?? String(failure.status ?? ''));
        setNota({ tipo: 'errore', testo: testoEsito(t, esito, 'social.sfide_annulla_generico') });
        // La lista si rilegge da sola anche dopo un RIFIUTO (RTK invalida i tag di una mutazione respinta con
        // un valore, come `revokeLimuloChallenge` dichiara): «l'altro ha gia' risposto» aggiorna la riga da
        // «in attesa» a com'e' davvero, senza che qui si rilegga a mano.
      })
      .finally(() => {
        inVolo.current = false;
      });
  };

  const vaiAlTorneo = () => {
    if (sfida.tournamentId === null) return;
    haptics.impact('light');
    navigate(limuloTournamentUrl(sfida.tournamentId));
  };

  const fase = state === 'accepted' ? faseDellaAccettata(sfida) : null;
  const statoTesto = (() => {
    switch (state) {
      case 'pending':
        return t('social.sfide_tab_stato_in_attesa');
      case 'accepted':
        return t('social.sfide_tab_stato_accettata', { name: nome });
      case 'declined':
        return t('social.sfide_tab_stato_rifiutata', { name: nome });
      case 'expired':
        return t('social.sfide_tab_stato_scaduta');
      case 'revoked':
        return t('social.sfide_tab_stato_annullata');
    }
  })();
  // L'esito positivo dell'annullamento sta sotto la riga solo finche' la lista non ha riletto: dopo, lo dice lo stato.
  const notaVisibile = nota !== null && !(nota.tipo === 'ok' && state !== 'pending') ? nota : null;
  const { evidenziataClasse, ...segno } = useRigaEvidenziata(evidenziata);
  if (Pelle?.RigaInviata) return <Pelle.RigaInviata vista={{ sfida, nome, nomeGioco, fase, statoTesto, notaVisibile, annullabile: staAnnullabile(state), revocaInVolo: esitoRevoca.isLoading, annulla, vaiAlTorneo, segno, evidenziataClasse }} />;

  return (
    <li
      {...segno}
      className={cn('rounded-xl bg-bg-secondary p-3', evidenziataClasse)}
      data-testid={`sfide-tab-riga-inviata-${challengeId}`}
      data-stato={state}
    >
      <div className="flex items-center gap-3">
        <IconaSfida />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg">{nome}</p>
          <p className="truncate text-xs text-fg-muted" data-testid={`sfide-tab-stato-${challengeId}`}>
            {nomeGioco} · {statoTesto}
          </p>
        </div>
        {staAnnullabile(state) && (
          <Button
            variant="neutro"
            size="sm"
            forma="pastiglia"
            peso="bold"
            disabled={esitoRevoca.isLoading}
            onClick={annulla}
            data-testid={`sfide-tab-annulla-${challengeId}`}
            aria-label={t('social.sfide_tab_annulla_aria', { name: nome })}
            className="shrink-0"
          >
            {t('social.sfide_tab_annulla')}
          </Button>
        )}
        {fase === 'pronta' && (
          <Button
            size="sm"
            forma="pastiglia"
            peso="bold"
            onClick={vaiAlTorneo}
            data-testid={`sfide-tab-vai-torneo-${challengeId}`}
            className="h-8 shrink-0 gap-1"
          >
            {t('social.sfide_tab_vai_partita')}
            <ChevronRight aria-hidden className="h-4 w-4" />
          </Button>
        )}
      </div>
      {fase === 'avvio' && (
        <p className="mt-2 text-xs text-fg-muted" data-testid={`sfide-tab-avvio-${challengeId}`}>
          {t('social.sfide_tab_accettata_avvio')}
        </p>
      )}
      {fase === 'non-partita' && (
        <p role="alert" className="mt-2 text-xs text-danger" data-testid={`sfide-tab-non-partita-${challengeId}`}>
          {t('social.sfide_tab_accettata_non_partita')}
        </p>
      )}
      {notaVisibile !== null && (
        <p
          role={notaVisibile.tipo === 'ok' ? 'status' : 'alert'}
          data-testid={`sfide-tab-nota-${challengeId}`}
          className={cn('mt-2 text-xs', notaVisibile.tipo === 'ok' ? 'font-medium text-brand' : 'text-danger')}
        >
          {notaVisibile.testo}
        </p>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pezzi comuni
// ─────────────────────────────────────────────────────────────────────────────

function IconaSfida() {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted">
      <Swords aria-hidden className="h-4 w-4" />
    </div>
  );
}

function ErroreDiLettura({ onRiprova, testid }: { onRiprova: () => void; testid: string }) {
  const { t } = useTranslation('general');
  return (
    <div
      role="alert"
      data-testid={testid}
      className="card-edge flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-4 text-center text-sm text-danger"
    >
      <p>{t('social.sfide_tab_errore')}</p>
      <Button variant="neutro" size="sm" forma="pastiglia" peso="bold" onClick={onRiprova}>
        {t('social.sfida_riprova')}
      </Button>
    </div>
  );
}
